import { useState, useEffect } from "react";
import { useAccount, useBalance } from "wagmi";
import { useInterval, useIdle } from "react-use";

// Utils
import { toFixedWithoutRounding } from "utils/common";

// Configs
import { config, currentNetworkInfo } from "config/network";

// Hooks
import useApp from "./useApp";

const POLLING_INTERVAL = 10000;

const initialData = {
  token: {
    symbol: config.nativeCurrency.symbol,
    icon: currentNetworkInfo.icon,
    balance: 0,
  },
  xtoken: {
    symbol: config.nativeCurrency.xsymbol,
    icon: currentNetworkInfo.tokenXIcon,
    balance: 0,
  },
};

const useTokenBalance = () => {
  const isIdle = useIdle(150e3);

  const { address, isConnected } = useAccount();
  const { pendingTxnCount } = useApp();

  const [tokenBalance, setTokenBalance] = useState(initialData);

  const {
    data: token,
    isLoading: isTokenLoading,
    refetch: tokenRefetch,
  } = useBalance({
    address: address,
    chainId: +config.chainId,
    enabled: isConnected,
  });

  const {
    data: xToken,
    isLoading: isXTokenLoading,
    refetch: xtokenRefetch,
  } = useBalance({
    address: address,
    token: config.contractAddresses.xtoken.token,
    chainId: +config.chainId,
    enabled: isConnected,
  });

  useEffect(() => {
    if (!isTokenLoading && !isXTokenLoading && isConnected && address) {
      setTokenBalance((prev) => ({
        token: {
          ...prev.token,
          balance: +toFixedWithoutRounding(token?.formatted || "0", 4),
        },
        xtoken: {
          ...prev.xtoken,
          balance: +toFixedWithoutRounding(xToken?.formatted || "0", 4),
        },
      }));
    } else if (!isConnected) {
      setTokenBalance(initialData);
    }
  }, [isTokenLoading, isXTokenLoading, token, xToken, isConnected, address]);

  useEffect(() => {
    if (pendingTxnCount) {
      (async function fetchBalance() {
        const [tokenBal, xtokenBal] = await Promise.all([
          tokenRefetch(),
          xtokenRefetch(),
        ]);
        setTokenBalance((prev) => ({
          token: {
            ...prev.token,
            balance: +toFixedWithoutRounding(
              tokenBal.data?.formatted || "0",
              4
            ),
          },
          xtoken: {
            ...prev.xtoken,
            balance: +toFixedWithoutRounding(
              xtokenBal.data?.formatted || "0",
              4
            ),
          },
        }));
      })();
    }
  }, [pendingTxnCount]);

  useInterval(() => {
    if (!isIdle && isConnected) {
      (async function fetchBalance() {
        const [tokenBal, xtokenBal] = await Promise.all([
          tokenRefetch(),
          xtokenRefetch(),
        ]);
        setTokenBalance((prev) => ({
          token: {
            ...prev.token,
            balance: +toFixedWithoutRounding(
              tokenBal.data?.formatted || "0",
              4
            ),
          },
          xtoken: {
            ...prev.xtoken,
            balance: +toFixedWithoutRounding(
              xtokenBal.data?.formatted || "0",
              4
            ),
          },
        }));
      })();
    }
  }, POLLING_INTERVAL);

  return tokenBalance;
};

export default useTokenBalance;
